// CoacDecisionField — the IM's corporate-action decision capture, and the one
// place that builds a corporate-action decision list.
//
// WHAT CHANGED IN SEP-251. The list used to be three fixed strings
// (`CA_DECISION_PRESETS`: "Take up / participate", "Take cash",
// "Decline / no action"). Every corporate action showed the same three, so a
// nine-option Dutch auction and a two-currency dividend offered the same
// choices, and neither offered the one the notice printed. The notice is the
// truth, so the list now comes from `CoacAction.electionOptions`, which the CEDAR
// extraction reads off the document and an Ops reviewer confirms at approval.
//
// THE LIST OF ONE ACTION IS THE LIST OF THAT ACTION. Nothing is generic and
// nothing carries over: a second corporate action shows its own options and no
// other. When the action prints no option (`[]`), or nobody read a notice
// (`null`), the printed part of the list is empty.
//
// THE TWO EXCLUSIONS. "Client Sold" and "Client Transferred Out" are always
// available for a PER-PORTFOLIO decision, in addition to the printed options.
// They are not elections: they say that the client does not hold the security
// any more, so the portfolio owes the custodian no election. They resolve the
// portfolio. The per-CA field below shows no exclusion, because the whole event
// is not sold; one portfolio is.
//
// "CUSTOM…" SURVIVES. It is how an IM records an answer that the notice does
// not print, for example a conditional client instruction or a partial
// quantity. Three facts keep it: an execution-only client can answer with
// something no option covers; an action whose options nobody read would
// otherwise offer nothing but the two exclusions, which is worse than today;
// and a free-text answer carries no option code, so the data still states
// plainly that it is not a printed option. It is no longer the default
// selection, and it sits last.

import { useId } from 'react';
import { Pill } from '@/components/ui';
import type { CoacAccountElectionRow, CoacActionRow } from '@septima/contract';

/** One printed election option of one corporate-action notice.
 *  Read off the wire row, because the contract package does not export the
 *  name on its own. */
export type CoacElectionOption = NonNullable<CoacActionRow['electionOptions']>[number];

/** Why a portfolio owes the custodian no election. */
export type CoacExclusionReason = NonNullable<CoacAccountElectionRow['exclusionReason']>;

/** The key of the free-text choice. It names no printed option. */
export const COAC_CUSTOM_CHOICE_KEY = 'custom';

/** The two exclusions, in the order that a person reads them. */
export const COAC_EXCLUSIONS: readonly { reason: CoacExclusionReason; label: string }[] = [
    { reason: 'client_sold', label: 'Client Sold' },
    { reason: 'client_transferred_out', label: 'Client Transferred Out' },
];

/**
 * One choice of a corporate-action decision list.
 *
 * `key` identifies the choice in a radio group or a select. `decision` is the
 * text that the election stores, so a reader that nobody updated shows the
 * words of the row. `electionOptionCode` and `exclusionReason` carry the
 * meaning, and at most one of them is set.
 */
export interface CoacDecisionChoice {
    key: string;
    label: string;
    decision: string;
    electionOptionCode: string | null;
    exclusionReason: CoacExclusionReason | null;
    isDefault: boolean;
}

/** The key of one printed option. */
function optionKey(code: string): string {
    return `option:${code}`;
}

/** The key of one exclusion. */
function exclusionKey(reason: CoacExclusionReason): string {
    return `exclusion:${reason}`;
}

/**
 * Build the decision list of ONE corporate action.
 *
 * @param options The options that the notice prints, in the printed order.
 *   `null` says that nobody read a notice, and `[]` says that the notice prints
 *   no option. Both give the same list here, because neither gives a choice.
 * @param withExclusions True for a per-portfolio list. The two exclusions then
 *   follow the printed options.
 * @returns The choices, in the order that a person reads them: the printed
 *   options first, then the exclusions.
 */
export function coacDecisionChoices(
    options: readonly CoacElectionOption[] | null | undefined,
    withExclusions: boolean,
): CoacDecisionChoice[] {
    const choices: CoacDecisionChoice[] = (options ?? []).map((o) => ({
        key: optionKey(o.code),
        label: o.label,
        decision: o.label,
        electionOptionCode: o.code,
        exclusionReason: null,
        isDefault: o.isDefault,
    }));
    if (!withExclusions) return choices;
    for (const e of COAC_EXCLUSIONS) {
        choices.push({
            key: exclusionKey(e.reason),
            label: e.label,
            decision: e.label,
            electionOptionCode: null,
            exclusionReason: e.reason,
            isDefault: false,
        });
    }
    return choices;
}

/**
 * The choice that a recorded election selects.
 *
 * It reads the CODES of the row and never its text, because the text of an
 * election is free and two firms can write the same fact in two ways.
 *
 * @returns The key of the choice, or the free-text key when the row names no
 *   printed option and no exclusion.
 */
export function coacRecordedChoiceKey(recorded: {
    electionOptionCode?: string | null;
    exclusionReason?: CoacExclusionReason | null;
}): string {
    if (recorded.exclusionReason) return exclusionKey(recorded.exclusionReason);
    if (recorded.electionOptionCode) return optionKey(recorded.electionOptionCode);
    return COAC_CUSTOM_CHOICE_KEY;
}

/** The text of one option in a `<select>`, with the default marked. A select
 *  holds text only, so the mark is a word and not a pill. */
export function coacChoiceOptionText(choice: CoacDecisionChoice): string {
    return choice.isDefault ? `${choice.label} (Default)` : choice.label;
}

/**
 * Says in plain words why an option list is not usable, for the Ops reviewer.
 *
 * The server is the enforcement point: `readCoacElectionOptions`
 * (`vendor/septima-ontology-wealth/packages/ontology-wealth/src/coac/election-option.ts`) refuses the approval
 * BEFORE any write, and the rules live there. This check is the same rules said
 * early, so the reviewer sees the problem beside the field and the Approve
 * button greys out. It never replaces the server gate.
 *
 * The "two defaults" rule needs no check here, because the reviewer picks the
 * default in one radio group and a radio group holds one answer.
 *
 * @returns The message, or null when the list is usable.
 */
export function coacElectionOptionsProblem(
    options: readonly CoacElectionOption[] | null,
): string | null {
    if (options === null) return null;
    const seen = new Set<string>();
    for (const o of options) {
        const code = o.code.trim();
        const label = o.label.trim();
        if (code === '') return 'An election option carries no code. Give each option its code.';
        if (label === '') {
            return `Option ${code} carries no text. Give each option the words of the notice.`;
        }
        if (seen.has(code)) return `Option ${code} occurs more than once. Each code is unique.`;
        seen.add(code);
    }
    return null;
}

interface CoacDecisionFieldProps {
    /** Current decision text (the parent owns this state). */
    value: string;
    onChange: (decision: string) => void;
    disabled?: boolean;
    /** True when a decision was ingested from the source file. */
    hasIngestedDecision?: boolean;
    /** The options that the notice of this corporate action prints. */
    options: readonly CoacElectionOption[] | null;
}

export default function CoacDecisionField({
    value,
    onChange,
    disabled,
    hasIngestedDecision,
    options,
}: CoacDecisionFieldProps) {
    const name = useId();
    // The per-CA field records one decision for the whole event, so it offers
    // the printed options and no exclusion.
    const choices = coacDecisionChoices(options, false);
    const matched = choices.find((c) => c.decision === value);
    // Free text is selected whenever the value is empty or matches no option.
    const selectedKey = matched?.key ?? COAC_CUSTOM_CHOICE_KEY;

    return (
        <fieldset className="mt-3" disabled={disabled}>
            <legend className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                IM decision
                {hasIngestedDecision && (
                    <span className="ml-2 text-[rgb(var(--color-text-secondary))]">
                        (ingested from source — edit to override)
                    </span>
                )}
            </legend>

            <div className="mt-2 flex flex-col gap-1.5">
                {choices.length === 0 && (
                    <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                        This notice prints no election option. Type the decision below.
                    </p>
                )}
                {choices.map((choice) => (
                    <label
                        key={choice.key}
                        className="flex items-center gap-2 text-body text-[rgb(var(--color-text-primary))] cursor-pointer"
                    >
                        <input
                            type="radio"
                            name={name}
                            checked={selectedKey === choice.key}
                            onChange={() => onChange(choice.decision)}
                            className="accent-[rgb(var(--color-accent))]"
                        />
                        {choice.label}
                        {choice.isDefault && <Pill variant="blue" label="Default" size="sm" />}
                    </label>
                ))}

                <label className="flex items-center gap-2 text-body text-[rgb(var(--color-text-primary))] cursor-pointer">
                    <input
                        type="radio"
                        name={name}
                        checked={selectedKey === COAC_CUSTOM_CHOICE_KEY}
                        // Selecting Custom clears a printed option so the IM
                        // types their own; an existing custom value stays.
                        onChange={() => onChange(matched ? '' : value)}
                        className="accent-[rgb(var(--color-accent))]"
                    />
                    Custom
                </label>

                {selectedKey === COAC_CUSTOM_CHOICE_KEY && (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full mt-1 px-3 py-2 bg-[rgb(var(--color-surface-secondary))] rounded-button text-body text-[rgb(var(--color-text-primary))]"
                        placeholder="An answer the notice does not print"
                    />
                )}
            </div>
        </fieldset>
    );
}
